/**
 * auth-layout.tsx — Đăng nhập / đăng ký / quên mật khẩu dạng HỘP NỔI trên nền giao diện hệ thống (kiểu SaleSmartly).
 *
 *  - `AuthShell`: route cha của /login, /register, /signup, /forgot-password. Nền giả lập giao diện chính
 *    (`AuthBackdrop`, tĩnh, không gọi API) luôn được giữ khi đổi qua lại giữa các trang con; chỉ nội dung hộp đổi.
 *  - `AuthDialogFrame`: khung hộp (Radix Dialog) — không đóng bằng Esc / bấm ra ngoài vì phải đăng nhập mới dùng được;
 *    giữ focus trong hộp. Điện thoại: phủ kín màn hình, lề 16px. Dùng lại cho hộp "Phiên đã hết hạn" trong hệ thống.
 *  - `AuthLayout`: phần nội dung trong hộp (logo, tiêu đề, mô tả, form, chân).
 */
import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { BrandLogo } from '@/components/shared/brand-logo'
import { Loading } from '@/components/shared/feedback'
import { getToken } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import { authRedirectTarget } from '@/components/shared/nav-config'
import { AuthBackdrop } from './auth-backdrop'
import { useTranslation } from 'react-i18next'
import { LanguageMenuButton } from '@/components/shared/language-select'

/** Ô nhập kiểu "nền xám đặc" trong hộp đăng nhập. */
export const AUTH_INPUT =
  'h-12 rounded-lg border-transparent bg-muted px-4 text-sm shadow-none hover:border-transparent focus-visible:border-primary focus-visible:bg-background'

/**
 * Chỉ cho khách chưa đăng nhập: đã đăng nhập → về trang định vào (D9 `authRedirectTarget`, mặc định `/` — trang đầu
 * theo quyền); đang kiểm phiên → chờ.
 */
export function PublicOnly({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const loading = useAuthStore((s) => s.loading)
  const location = useLocation()
  const { t } = useTranslation('auth')
  // Đăng nhập / đăng ký xong (kể cả qua OTP, Google) → `?next=` nếu có, không thì trang bị chặn trước đó.
  if (user) return <Navigate to={authRedirectTarget(location.state, location.search)} replace />
  if (getToken() && loading) {
    return (
      <>
        <DialogPrimitive.Title className="sr-only">{t('checkingSession')}</DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">{t('pleaseWait')}</DialogPrimitive.Description>
        <Loading label={t('checkingSessionDots')} />
      </>
    )
  }
  return <>{children}</>
}

/** Route cha của các trang công khai: nền giả lập hệ thống + hộp đăng nhập (nền không bị gỡ khi đổi trang con). */
export function AuthShell() {
  const { pathname } = useLocation()
  return (
    <>
      <AuthBackdrop />
      <AuthDialogFrame>
        <PublicOnly>
          {/* key theo đường dẫn → mỗi lần đổi Đăng nhập ↔ Đăng ký ↔ Quên mật khẩu có hiệu ứng mờ dần nhẹ */}
          <div key={pathname} className="animate-fade-in">
            <Outlet />
          </div>
        </PublicOnly>
      </AuthDialogFrame>
    </>
  )
}

const block = (e: Event) => e.preventDefault()

export function AuthDialogFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/45 backdrop-blur-[3px] animate-fade-in" />
        <DialogPrimitive.Content
          onEscapeKeyDown={block}
          onPointerDownOutside={block}
          onInteractOutside={block}
          className={cn(
            'fixed inset-0 z-50 overflow-y-auto bg-background px-4 pb-8 pt-12 text-foreground outline-none animate-fade-in',
            'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[92vh] sm:w-[calc(100%-2rem)] sm:max-w-[560px] sm:-translate-x-1/2 sm:-translate-y-1/2',
            'sm:rounded-2xl sm:px-12 sm:pb-10 sm:pt-12 sm:shadow-[0_24px_80px_-12px_rgb(0_0_0/0.35)]',
            className,
          )}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-primary/15 via-primary/5 to-transparent sm:rounded-t-2xl"
          />
          {/* Chọn ngôn ngữ (chưa đăng nhập → chỉ lưu trên trình duyệt; đăng nhập xong đồng bộ lên tài khoản khi đổi lại). */}
          <LanguageMenuButton className="absolute right-4 top-4 z-10 sm:right-5 sm:top-5" />
          <div className="relative">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export function AuthLayout({ title, subtitle, children, footer }: {
  title: string
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <div>
      <div className="mb-6 flex justify-center">
        <BrandLogo className="justify-center" imgClassName="h-20 max-w-[280px] sm:h-24 sm:max-w-[340px]" />
      </div>
      <DialogPrimitive.Title className="text-center text-2xl font-bold tracking-tight sm:text-[28px] sm:leading-tight">{title}</DialogPrimitive.Title>
      <DialogPrimitive.Description className={subtitle ? 'mt-2 text-center text-sm text-muted-foreground' : 'sr-only'}>
        {subtitle ?? title}
      </DialogPrimitive.Description>
      <div className="mt-7">{children}</div>
      {footer && <div className="mt-7 text-center text-sm text-muted-foreground">{footer}</div>}
    </div>
  )
}

export function FieldError({ msg }: { msg?: string | null }) {
  if (!msg) return null
  return <p className="text-xs text-destructive">{msg}</p>
}
